import { useState } from 'react';
import { toast } from 'sonner';
import { submitEnquiry } from '../lib/api';
import { OCCASIONS, BUDGETS, STYLES, READY_TIMES } from '../lib/constants';
import { Loader2 } from 'lucide-react';
import { trackEvent } from '../lib/analytics';

const initial = {
  name: '', phone: '', email: '',
  occasion: '', event_date: '', venue: '', city: '',
  budget: '', ready_time: '', location_pref: '', makeup_style: '',
  reference_notes: '',
};

export const SkipSearchForm = ({ variant = 'cream' }) => {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm(s => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.occasion) {
      toast.error('Please share your name, phone and occasion.');
      return;
    }
    setBusy(true);
    try {
      await submitEnquiry(form);
      trackEvent('enquiry_submit', { city: form.city, occasion: form.occasion, budget: form.budget });
      toast.success("Thank you — we'll be in touch within 24 hours.");
      setForm(initial);
    } catch (err) {
      toast.error('Could not send — please try WhatsApp.');
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    'w-full bg-transparent border-0 border-b border-neutral-300 focus:border-[#eb5381] outline-none px-0 py-3 text-base placeholder:text-neutral-400 font-sans';
  const labelCls = 'block text-[11px] uppercase tracking-[0.2em] text-neutral-600 mb-1';

  return (
    <form
      onSubmit={submit}
      data-testid="skip-search-form"
      className={`${variant === 'cream' ? 'bg-[#ffefd1]' : 'bg-white'} p-8 md:p-14 lg:p-20`}
    >
      <div className="max-w-3xl">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#eb5381]">Skip the Search</div>
        <h2 className="mt-3 font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
          Tell us your requirements — <span className="italic text-[#a465a8]">we'll find your perfect MUA.</span>
        </h2>
        <p className="mt-5 text-neutral-700 max-w-xl text-base md:text-lg leading-relaxed">
          Share a few details and our concierge team will hand-pick three verified artists matched to your event, budget and style.
        </p>
      </div>

      <div className="mt-12 grid md:grid-cols-2 gap-x-10 gap-y-7">
        <div>
          <label className={labelCls}>Your Name *</label>
          <input data-testid="enquiry-name" required value={form.name} onChange={set('name')} className={inputCls} placeholder="Aanya Sharma" />
        </div>
        <div>
          <label className={labelCls}>Phone (WhatsApp) *</label>
          <input data-testid="enquiry-phone" required value={form.phone} onChange={set('phone')} className={inputCls} placeholder="+91 9XXXXXXXXX" />
        </div>
        <div>
          <label className={labelCls}>Email</label>
          <input type="email" data-testid="enquiry-email" value={form.email} onChange={set('email')} className={inputCls} placeholder="you@email.com" />
        </div>
        <div>
          <label className={labelCls}>Occasion *</label>
          <select data-testid="enquiry-occasion" required value={form.occasion} onChange={set('occasion')} className={inputCls}>
            <option value="">Select occasion</option>
            {OCCASIONS.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Event Date</label>
          <input type="date" data-testid="enquiry-date" value={form.event_date} onChange={set('event_date')} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>City</label>
          <input data-testid="enquiry-city" value={form.city} onChange={set('city')} className={inputCls} placeholder="Mumbai, Delhi, etc." />
        </div>
        <div>
          <label className={labelCls}>Venue</label>
          <input data-testid="enquiry-venue" value={form.venue} onChange={set('venue')} className={inputCls} placeholder="The Taj Mahal Palace, Colaba" />
        </div>
        <div>
          <label className={labelCls}>Budget</label>
          <select data-testid="enquiry-budget" value={form.budget} onChange={set('budget')} className={inputCls}>
            <option value="">Select budget</option>
            {BUDGETS.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Ready Time</label>
          <select data-testid="enquiry-ready" value={form.ready_time} onChange={set('ready_time')} className={inputCls}>
            <option value="">When do you need to be ready?</option>
            {READY_TIMES.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Preferred Location</label>
          <select data-testid="enquiry-location" value={form.location_pref} onChange={set('location_pref')} className={inputCls}>
            <option value="">Select preference</option>
            <option value="Artist Studio">Artist Studio</option>
            <option value="At My Venue">At My Venue</option>
            <option value="Either">Either</option>
          </select>
        </div>
        <div>
          <label className={labelCls}>Makeup Style</label>
          <select data-testid="enquiry-style" value={form.makeup_style} onChange={set('makeup_style')} className={inputCls}>
            <option value="">Select style</option>
            {STYLES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="md:col-span-2">
          <label className={labelCls}>Reference / Notes</label>
          <textarea
            data-testid="enquiry-notes"
            value={form.reference_notes}
            onChange={set('reference_notes')}
            rows={3}
            className={inputCls + ' resize-none'}
            placeholder="Drop Pinterest links, mood references, skin concerns, or specific requests..."
          />
        </div>
      </div>

      <div className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-5">
        <button
          type="submit"
          disabled={busy}
          data-testid="enquiry-submit"
          className="inline-flex items-center gap-3 bg-[#eb5381] hover:bg-[#d64570] disabled:opacity-60 text-white px-10 py-4 text-[12px] uppercase tracking-[0.25em] transition-colors"
        >
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}
          Send My Brief
        </button>
        <p className="text-xs text-neutral-600 max-w-md">
          No spam — only personally curated artist suggestions. We typically respond within 24 hours.
        </p>
      </div>
    </form>
  );
};
